import { Dictionary, identity, isEmpty, omit, omitBy, pickBy } from 'lodash';
import { nanoid } from 'nanoid';

import { WithId } from '@components/base';
import FilterDefinition, { LogicalOperator } from '@models/FilterDefinition';
import { FilterField, FilterGroup, FilterGroupWithoutId } from '@models/SearchFilter';
import { TitleTypes } from '@models/Title';
import { getFilterCount } from '@modules/layouts/hooks/useSearchAndFilter/utils';

export const defaultLogicalOperator: LogicalOperator = 'and';

export const getNewFilterGroup = (
  isRootLevelFilter: boolean | undefined = false,
  logicalOperator: LogicalOperator | undefined = defaultLogicalOperator
): FilterGroup => ({ fields: [], isRootLevelFilter, logicalOperator });

export const getNewFilterField = (isRootLevelFilter?: boolean): FilterField => ({
  id: nanoid(6),
  ...(isRootLevelFilter
    ? { filter: getNewFilterGroup(isRootLevelFilter) }
    : { propertyName: '', value: '', operator: 'eq' }),
});

export const isEmptyString = (value: unknown): boolean => value === '';

// Removes all null values
export const sanitize = <T>(obj: T, removeEmptyStrings?: boolean): T =>
  JSON.parse(
    JSON.stringify(obj, (_, value) =>
      value == null
        ? undefined
        : removeEmptyStrings
          ? isEmptyString(value)
            ? undefined
            : value
          : value
    )
  );

// Removes all falsy values, including empty nested objects and arrays
export const deepClean = (obj: Dictionary<unknown>): Dictionary<unknown> =>
  omitBy(pickBy(sanitize(obj, true), identity), isEmpty);

export function removeId<T>(item: WithId<T>): T {
  return omit(item, 'id') as T;
}

function addId<T>(item: T, id?: string): WithId<T> {
  return { ...item, id: id ?? nanoid(5) };
}

export const removeFilterIds = (
  filter: FilterGroup | FilterGroupWithoutId
): FilterGroupWithoutId =>
  filter.fields && filter.fields.length > 0
    ? {
        ...filter,
        fields: filter.fields.map(field => {
          const tmp = omit(field, 'id');
          if (field.filter) return { ...tmp, filter: removeFilterIds(field.filter) };
          return tmp;
        }),
      }
    : filter;

export const addFilterIds = (filter: FilterGroupWithoutId): FilterGroup =>
  filter.fields && filter.fields.length > 0
    ? {
        ...filter,
        fields: filter.fields.map(field => {
          const tmp = addId(field, (field as FilterField).id);
          if (tmp.filter) return { ...tmp, filter: addFilterIds(tmp.filter) };
          return tmp as FilterField;
        }),
      }
    : (filter as FilterGroup);

export function removeIdsFromList<T>(list?: WithId<T>[]): T[] {
  return list?.map(item => removeId(item)) ?? [];
}

export function addIdsToList<T>(list?: T[]): WithId<T>[] {
  return list?.map(item => addId(item)) ?? [];
}

export const initializeCollectionFilters = (filter?: FilterGroup) =>
  filter && getFilterCount(filter) ? addFilterIds(filter) : getNewFilterGroup(true);

export function removeEmptyParams<T>(params: T): Partial<T> {
  return JSON.parse(JSON.stringify(params, (_, value) => (value === '' ? undefined : value)));
}

// Recursive filter generation functions
export const findPropertyLocInFilterDefs = (
  filterDefs: FilterDefinition[],
  propertyName: string,
  path: string[] | undefined = []
): string[] => {
  const filterDef = filterDefs.find(filter => JSON.stringify(filter).includes(`"${propertyName}"`));

  if (filterDef) {
    const tmpPath = [...path, filterDef.propertyName];

    return filterDef.fields && filterDef.fields.length > 0
      ? findPropertyLocInFilterDefs(filterDef.fields, propertyName, tmpPath)
      : tmpPath;
  }

  return path;
};

export const getFilterGroup = (
  filterDefs: FilterDefinition[],
  values: string[],
  path: string[],
  isRootLevelFilter: boolean,
  logicalOperator?: LogicalOperator
): FilterGroup | undefined => {
  const propertyName = path.at(0);
  const filterDef = filterDefs.find(fd => fd.propertyName === propertyName);

  if (filterDef)
    return {
      // eslint-disable-next-line no-use-before-define
      fields: getFilterFields(filterDefs, values, path),
      isRootLevelFilter,
      logicalOperator: logicalOperator ?? (isRootLevelFilter ? 'and' : 'or'),
    };

  return undefined;
};

export const getFilterFields = (
  filterDefs: FilterDefinition[],
  values: string[],
  path: string[]
): FilterField[] => {
  const propertyName = path.shift();
  const filterDef = filterDefs.find(fd => fd.propertyName === propertyName);

  if (filterDef)
    return path.length > 0
      ? [
          {
            filter: getFilterGroup(filterDef.fields, values, path, false),
            id: nanoid(6),
            operator: filterDef.operators.at(0),
            propertyName,
          },
        ]
      : values.map(value => ({
          id: nanoid(6),
          operator: filterDef.operators.at(0),
          propertyName,
          value,
        }));

  return [];
};

export const generateFilter = (
  filterDefs: FilterDefinition[],
  propertyName: string,
  values: string[],
  isRootLevelFilter: boolean | undefined = true,
  logicalOperator: LogicalOperator | undefined = 'and'
): FilterGroup | undefined =>
  getFilterGroup(
    filterDefs,
    values,
    findPropertyLocInFilterDefs(filterDefs, propertyName),
    isRootLevelFilter,
    logicalOperator
  );

// Filter functions utilizing generation functions above
export const getFilterWithProvider = (
  filterDefs: FilterDefinition[],
  filter: FilterGroup,
  providerIdentifier?: string
): FilterGroup => {
  if (providerIdentifier) {
    const hasFilter = filter?.fields.length > 0;
    const providerFilter = generateFilter(
      filterDefs,
      'ProviderIdentifier',
      [providerIdentifier],
      true
    );

    if (providerFilter)
      return hasFilter
        ? {
            fields: [
              { id: nanoid(6), filter: providerFilter },
              { id: nanoid(6), filter: { ...filter, isRootLevelFilter: true } },
            ],
            logicalOperator: 'and',
            isRootLevelFilter: true,
          }
        : providerFilter;
  }
  return filter;
};

export const combineFilterFields = (
  fields: FilterField[],
  logicalOperator: LogicalOperator | undefined = 'and'
) =>
  ({
    fields,
    logicalOperator,
    isRootLevelFilter: true,
  }) as FilterGroup;

export const combineFilterGroups = (...filterGroups: FilterGroup[]) =>
  combineFilterFields(filterGroups.map(filter => ({ id: nanoid(6), filter })));

export const getTitlesFromProviderFilter = (
  filterDefs: FilterDefinition[],
  titleType?: string,
  providerIdentifier?: string
): FilterGroup =>
  combineFilterFields([
    // Filter for title types (movie/series/program/episode)
    {
      id: nanoid(6),
      filter: getFilterGroup(
        filterDefs,
        titleType ? [titleType] : Array.from(TitleTypes),
        findPropertyLocInFilterDefs(filterDefs, 'Type'),
        false
      ),
    },
    // Filter for provider identifier (if any)
    ...(providerIdentifier
      ? getFilterFields(
          filterDefs,
          [providerIdentifier],
          findPropertyLocInFilterDefs(filterDefs, 'ProviderIdentifier')
        )
      : []),
  ]);

export const getTitlesFromSourceFilter = (
  filterDefs: FilterDefinition[],
  titleType?: string,
  sourceId?: string
): FilterGroup =>
  combineFilterFields([
    // Filter for title types (movie/series/program/episode)
    {
      id: nanoid(6),
      filter: getFilterGroup(
        filterDefs,
        titleType ? [titleType] : Array.from(TitleTypes),
        findPropertyLocInFilterDefs(filterDefs, 'Type'),
        false
      ),
    },
    // Filter for sourceId (if any)
    ...(sourceId
      ? getFilterFields(filterDefs, [sourceId], findPropertyLocInFilterDefs(filterDefs, 'SourceId'))
      : []),
  ]);
